import React from 'react';

import {
  Box,
  Button,
  Typography,
  TypographyProps,
  Modal,
  Stack,
  IconButton,
} from '@mui/material';

import { ReactComponent as CloseIcon } from '../assets/icons/cancel_icon_blue.svg';

const modalStyle = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
  borderRadius: '16px',
  p: 4,
  minWidth: '300px',
  maxWidth: '1200px',
  maxHeight: 'calc(100% - 40px)',
  overflowY: 'auto',
};

type Props = {
  title: string;
};

const TextViewableInPopup: React.FC<Props & TypographyProps> = (props) => {
  const { children } = props;
  const divRef = React.useRef<HTMLElement>();
  const [overflowActive, setOverflowActive] = React.useState(false);
  const [showMore, setShowMore] = React.useState(false);

  React.useEffect(() => {
    const checkOverflow = (textContainer: HTMLElement | undefined) => {
      if (textContainer) {
        return (
          textContainer.offsetHeight < textContainer.scrollHeight ||
          textContainer.offsetWidth < textContainer.scrollWidth
        );
      }
      return false;
    };

    const handleResize = () => {
      checkOverflow(divRef.current)
        ? setOverflowActive(true)
        : setOverflowActive(false);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Box
      sx={{
        width: '100%',
      }}
    >
      <Box
        ref={divRef}
        sx={{
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: '5', // Show maximum 5 lines
          WebkitBoxOrient: 'vertical',
          width: '100%',
          overflow: 'hidden',
          flexDirection: 'row',
          textAlign: 'left',
          pr: 0,
        }}
      >
        <pre
          style={{
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word',
            flexGrow: 1,
            position: 'relative',
            fontFamily: 'SFProDisplay-Medium',
            color: '#fff',
            fontSize: 16,
            lineHeight: 1.4,
            marginTop: 4,
            marginBottom: 4,
            marginRight: 0,
            marginLeft: 0,
            height: '100%',
          }}
          className='small-font-on-mobile'
        >
          {children}
        </pre>
      </Box>
      {overflowActive && (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-start',
            paddingTop: 4,
          }}
        >
          <Button
            onClick={() => setShowMore(!showMore)}
            variant='text'
            className='show-more-button'
          >
            Show more
          </Button>
        </div>
      )}

      <Modal open={showMore} onClose={() => setShowMore(false)}>
        <Box sx={modalStyle}>
          <Stack spacing={4}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography
                textAlign='left'
                fontFamily='SFProDisplay-Semibold'
                variant='h5'
              >
                {props.title}
              </Typography>
              <IconButton onClick={() => setShowMore(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box>
              <pre
                style={{
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  flexGrow: 1,
                  position: 'relative',
                  fontFamily: 'SFProDisplay-Medium',
                  color: '#002145B2',
                  fontSize: 16,
                  lineHeight: 1.6,
                  marginBottom: 4,
                  marginRight: 8,
                  marginLeft: 8,
                  height: '100%',
                }}
              >
                {children}
              </pre>
            </Box>
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
};

export default TextViewableInPopup;
